import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { categories as mockCategories } from '@/mocks/categories'

export default function Navbar() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [catOpen, setCatOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setCatOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  const isActive = (path: string) => location.pathname === path

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-primary-500 flex items-center justify-center">
            <i className="ri-flashlight-fill text-background-50 text-lg md:text-xl"></i>
          </div>
          <span className="font-heading text-xl md:text-2xl text-foreground-950">
            快猫短视频
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              isActive('/')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
            }`}
          >
            {t('nav.home')}
          </Link>

          <div className="relative">
            <button
              onClick={() => setCatOpen(!catOpen)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                location.pathname.startsWith('/category')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              {t('nav.categories')}
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {catOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setCatOpen(false)}></div>
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200 rounded-xl shadow-lg z-50 py-2">
                  {mockCategories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                      <span>{cat.name}</span>
                      <span className="text-xs text-foreground-400 ml-auto">{cat.count.toLocaleString()}</span>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          <Link
            to="/about"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              isActive('/about')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
            }`}
          >
            {t('nav.about')}
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-download-2-line mr-1.5"></i>
            {t('nav.download')}
          </button>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 flex flex-col gap-1">
          <Link to="/" className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap">
            {t('nav.home')}
          </Link>
          <div className="px-4 py-3 text-sm font-medium text-foreground-500 whitespace-nowrap">
            {t('nav.categories')}
          </div>
          {mockCategories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-3 pl-8 pr-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 whitespace-nowrap"
            >
              <i className={`${cat.icon} text-base text-primary-500`}></i>
              {cat.name}
            </Link>
          ))}
          <Link to="/about" className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap">
            {t('nav.about')}
          </Link>
          <button className="mt-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-download-2-line mr-1.5"></i>
            {t('nav.download')}
          </button>
        </div>
      )}
    </nav>
  )
}